/**
 * Supplier Relationships section for the customer settings tab.
 * Manages per-site settings including customer codes and currency.
 */

import { Fragment } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Loader2, Handshake, ChevronDown, ChevronRight, Users } from 'lucide-react';
import { CustomerSupplierRelationship, Site } from '../../lib/api';

interface SupplierRelationshipsSectionProps {
  relationships: CustomerSupplierRelationship[];
  relLoading: boolean;
  expandedRelId: number | null;
  setExpandedRelId: (id: number | null) => void;
  relSaving: number | null;
  canEditSettings: boolean;
  assignDialogOpen: boolean;
  setAssignDialogOpen: (open: boolean) => void;
  availableSites: Site[];
  selectedSiteId: number | null;
  setSelectedSiteId: (id: number | null) => void;
  assigning: boolean;
  setRelationships: React.Dispatch<React.SetStateAction<CustomerSupplierRelationship[]>>;
  updateRelationship: (id: number, data: Partial<CustomerSupplierRelationship>) => void;
  removeRelationship: (id: number) => void;
  openAssignDialog: () => void;
  assignSite: () => void;
}

export function SupplierRelationshipsSection({
  relationships, relLoading, expandedRelId, setExpandedRelId,
  relSaving, canEditSettings,
  assignDialogOpen, setAssignDialogOpen, availableSites,
  selectedSiteId, setSelectedSiteId, assigning,
  setRelationships, updateRelationship, removeRelationship,
  openAssignDialog, assignSite,
}: SupplierRelationshipsSectionProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2"><Handshake className="h-5 w-5" />Supplier Relationships</CardTitle>
          <CardDescription>Per-supplier settings including customer codes and currency</CardDescription>
        </div>
        {canEditSettings && (<Button variant="outline" size="sm" onClick={openAssignDialog}>+ Assign Supplier</Button>)}
      </CardHeader>
      <CardContent>
        {relLoading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : relationships.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Handshake className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No supplier relationships found</p>
            {canEditSettings && <p className="text-sm mt-1">Click &quot;Assign Supplier&quot; to link a supplier to this customer</p>}
          </div>
        ) : (
          <div className="space-y-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Customer Code</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Buyer</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {relationships.map((rel) => {
                  const isExpanded = expandedRelId === rel.id;
                  return (
                    <Fragment key={rel.id}>
                      <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => setExpandedRelId(isExpanded ? null : rel.id)}>
                        <TableCell>{isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</TableCell>
                        <TableCell className="font-medium">{rel.supplier_name}</TableCell>
                        <TableCell>{rel.customer_code || '-'}</TableCell>
                        <TableCell>{rel.currency || '-'}</TableCell>
                        <TableCell>
                          <Badge className={rel.is_active ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800'}>
                            {rel.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>{rel.buyer_name || <span className="text-muted-foreground italic">Not assigned</span>}</TableCell>
                      </TableRow>

                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={6} className="bg-muted/20 p-0">
                            <div className="px-8 py-5 space-y-5">
                              {/* Codes & Currency */}
                              <div className="grid gap-4 md:grid-cols-2">
                                <div className="space-y-1.5">
                                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Customer Code</label>
                                  <input type="text" value={rel.customer_code} onChange={(e) => setRelationships((prev) => prev.map((r) => r.id === rel.id ? { ...r, customer_code: e.target.value } : r))} onBlur={() => updateRelationship(rel.id, { customer_code: rel.customer_code })} className="w-full px-3 py-2 border rounded-md text-sm bg-background" disabled={!canEditSettings} />
                                </div>
                                <div className="space-y-1.5">
                                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Currency</label>
                                  <select value={rel.currency} onChange={(e) => updateRelationship(rel.id, { currency: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm bg-background" disabled={!canEditSettings}>
                                    <option value="">Not set</option>
                                    {['GBP', 'USD', 'EUR', 'PLN', 'CAD', 'AUD', 'CHF', 'SEK', 'NOK', 'DKK', 'CZK', 'JPY', 'CNY', 'INR'].map(c => <option key={c} value={c}>{c}</option>)}
                                  </select>
                                </div>
                              </div>

                              {/* Buyer */}
                              <div>
                                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-2">Assigned Personnel</label>
                                <div className="grid gap-3 md:grid-cols-3 text-sm">
                                  <div className="flex items-center gap-2 p-2 border rounded-md">
                                    <Users className="h-4 w-4 text-muted-foreground" />
                                    <div>
                                      <p className="text-xs text-muted-foreground">Buyer</p>
                                      <p className="font-medium">{rel.buyer_name || 'Not assigned'}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center justify-between">
                                {relSaving === rel.id && (
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Saving...</div>
                                )}
                                {canEditSettings && (
                                  <Button variant="destructive" size="sm" className="ml-auto" onClick={(e) => { e.stopPropagation(); removeRelationship(rel.id); }}>Remove Supplier</Button>
                                )}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Assign Supplier Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Supplier</DialogTitle>
            <DialogDescription>Select a supplier to link to this customer company.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {availableSites.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No available suppliers to assign</p>
            ) : (
              <select value={selectedSiteId ?? ''} onChange={(e) => setSelectedSiteId(e.target.value ? parseInt(e.target.value) : null)} className="w-full px-3 py-2 border rounded-md text-sm bg-background">
                <option value="">Select a supplier...</option>
                {availableSites.map((site) => (<option key={site.id} value={site.id}>{site.name} ({site.code})</option>))}
              </select>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button onClick={assignSite} disabled={!selectedSiteId || assigning}>
              {assigning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
